// src/pages/admin/billing/AdminBillingInvoicesPage.js
import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminData } from '../../../contexts/AdminDataContext';
import InvoiceForm from '../../../components/admin/billing/invoice/InvoiceForm';
import PDFGenerator from '../../../utils/PDFGenerator';
import pb from '../../../lib/pocketbase';

function AdminBillingInvoicesPage() {
  const { loading: adminLoading, customers: ctxCustomers, vehicles: ctxVehicles, quotes: ctxQuotes, invoices: ctxInvoices } = useAdminData() || {};
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [editingInvoice, setEditingInvoice] = React.useState(null);
  const [editingInstallments, setEditingInstallments] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [error, setError] = React.useState('');

  const [settings, setSettings] = React.useState({ invoice_prefix: 'FACT', invoice_counter: 1, default_tax_rate: 20, invoice_payment_terms: 30 });
  const [settingsId, setSettingsId] = React.useState(null);
  const [customers, setCustomers] = React.useState(() => Array.isArray(ctxCustomers) ? ctxCustomers : []);
  const [vehicles, setVehicles] = React.useState(() => Array.isArray(ctxVehicles) ? ctxVehicles : []);
  const [services, setServices] = React.useState([]);
  const [parts, setParts] = React.useState([]);
  const [quotes, setQuotes] = React.useState(() => Array.isArray(ctxQuotes) ? ctxQuotes : []);
  const [invoices, setInvoices] = React.useState(() => Array.isArray(ctxInvoices) ? ctxInvoices : []);
  const [defaultCustomerIdFromQuery, setDefaultCustomerIdFromQuery] = React.useState('');
  const [showInvoicesGeneralQR, setShowInvoicesGeneralQR] = React.useState(false);
  const [historyInvoice, setHistoryInvoice] = React.useState(null);
  const [invoiceLogs, setInvoiceLogs] = React.useState([]);
  const [invoiceLogsLoading, setInvoiceLogsLoading] = React.useState(false);
  // Installments state
  const [showInstallmentsModal, setShowInstallmentsModal] = React.useState(false);
  const [activeInstallmentsInvoice, setActiveInstallmentsInvoice] = React.useState(null);
  const [installments, setInstallments] = React.useState([]);
  const [installmentsLoading, setInstallmentsLoading] = React.useState(false);
  const [genPlan, setGenPlan] = React.useState({ count: 3, frequency: 'monthly', start_date: new Date().toISOString().split('T')[0], deposit_enabled: false, deposit_type: 'percent', deposit_value: 30 });

  const customerNameOf = React.useCallback((inv) => (
    inv?.expand?.customer_id?.name || (customers.find(c => c.id === inv?.customer_id)?.name) || inv?.customer_name || '—'
  ), [customers]);

  const companyNameOf = React.useCallback((inv) => (
    inv?.expand?.customer_id?.company || (customers.find(c => c.id === inv?.customer_id)?.company) || ''
  ), [customers]);

  const projectNameOf = React.useCallback((inv) => {
    const pid = inv?.project_id || inv?.vehicle_id;
    if (!pid) return '';
    const p = vehicles.find(v => v.id === pid);
    return p?.name || p?.brand || '';
  }, [vehicles]);

  const logActivity = async ({ recordId, action, message, context = {} }) => {
    try {
      await pb.collection('activity_logs').create({
        collection: 'invoices',
        record_id: recordId,
        action,
        message,
        context
      });
    } catch (e) { /* noop */ }
  };

  const loadInstallments = async (invoiceId) => {
    if (!invoiceId) return [];
    setInstallmentsLoading(true);
    try {
      const list = await pb.collection('invoice_installments').getFullList({
        filter: `invoice_id = "${invoiceId}"`,
        sort: 'due_date'
      }).catch(() => []);
      setInstallments(list || []);
      return list || [];
    } catch {
      setInstallments([]);
      return [];
    } finally {
      setInstallmentsLoading(false);
    }
  };

  const openInstallments = async (invoice) => {
    setActiveInstallmentsInvoice(invoice);
    // default generator plan from invoice fields when available
    try {
      const cnt = Number(invoice?.installments_count || 3);
      const start = (() => {
        let d = null;
        if (invoice?.first_due_date) d = new Date(invoice.first_due_date);
        if (!d || isNaN(d.getTime())) {
          if (invoice?.due_date) d = new Date(invoice.due_date);
        }
        if (!d || isNaN(d.getTime())) d = new Date();
        return d.toISOString().split('T')[0];
      })();
      setGenPlan(prev => ({ ...prev, count: Math.max(1, cnt), start_date: start }));
    } catch { }
    await loadInstallments(invoice.id);
    setShowInstallmentsModal(true);
  };

  const recomputeInvoiceFromInstallments = async (invoice) => {
    try {
      const list = await pb.collection('invoice_installments').getFullList({ filter: `invoice_id = "${invoice.id}"` });
      const totalPaid = list.reduce((s, it) => s + Number(it.amount_paid || (it.status === 'paid' ? it.amount_due || 0 : 0)), 0);
      const total = Number(invoice.total || 0);
      const balance = Math.max(0, total - totalPaid);
      let payment_status = 'pending';
      const hasOverdue = list.some(it => (it.status === 'pending' || it.status === 'partial') && it.due_date && new Date(it.due_date) < new Date());
      if (balance <= 0.01) payment_status = 'paid';
      else if (totalPaid > 0 && totalPaid < total) payment_status = hasOverdue ? 'overdue' : 'partial';
      else if (hasOverdue) payment_status = 'overdue';
      await pb.collection('invoices').update(invoice.id, { total_paid: totalPaid, balance_due: balance, payment_status });
      await refreshInvoices();
    } catch { }
  };

  const generateInstallments = async () => {
    const inv = activeInstallmentsInvoice;
    if (!inv) return;
    try {
      const n = Math.max(1, Number(genPlan.count || 1));
      const start = new Date(genPlan.start_date || new Date());
      const total = Number(inv.total || 0);
      const parts = new Array(n).fill(0);
      // Compute deposit if enabled
      let deposit = 0;
      if (genPlan.deposit_enabled) {
        if (genPlan.deposit_type === 'percent') {
          deposit = Math.max(0, Math.min(total, Number(((total * Number(genPlan.deposit_value || 0)) / 100).toFixed(2))));
        } else {
          deposit = Math.max(0, Math.min(total, Number(Number(genPlan.deposit_value || 0).toFixed(2))));
        }
      }
      // First installment = deposit (if any), otherwise equal parts
      if (deposit > 0) {
        parts[0] = deposit;
        const rest = Math.max(0, total - deposit);
        const m = Math.max(1, n - 1);
        const base = Math.floor((rest / m) * 100) / 100;
        for (let i = 1; i < n; i++) parts[i] = base;
        const sumRest = parts.slice(1).reduce((a, b) => a + b, 0);
        parts[n - 1] = Number((rest - sumRest + parts[n - 1]).toFixed(2));
      } else {
        const base = Math.floor((total / n) * 100) / 100;
        for (let i = 0; i < n; i++) parts[i] = base;
        const sum = parts.reduce((a, b) => a + b, 0);
        parts[n - 1] = Number((total - sum + parts[n - 1]).toFixed(2));
      }
      const created = [];
      for (let i = 0; i < n; i++) {
        const due = new Date(start);
        if (genPlan.frequency === 'monthly') due.setMonth(due.getMonth() + i);
        else if (genPlan.frequency === 'biweekly') due.setDate(due.getDate() + i * 14);
        else if (genPlan.frequency === 'weekly') due.setDate(due.getDate() + i * 7);
        const payload = {
          invoice_id: inv.id,
          customer_id: inv.customer_id || undefined,
          project_id: inv.project_id || inv.vehicle_id || undefined,
          label: n === 1 ? 'Paiement' : (i === 0 && deposit > 0 ? 'Acompte' : `Échéance ${i + 1}/${n}`),
          due_date: (due && !isNaN(due.getTime())) ? due.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          amount_due: Number(parts[i].toFixed(2)),
          amount_paid: 0,
          status: 'pending',
          ...(deposit > 0 && i === 0 ? { is_deposit: true } : {})
        };
        try { const r = await pb.collection('invoice_installments').create(payload); created.push(r); } catch (e) { console.error('create installment', e); }
      }
      // Save deposit meta on invoice if fields exist
      try {
        const meta = { installments_generated: true };
        if (deposit > 0) {
          meta.deposit_expected = Number(deposit.toFixed(2));
          meta.deposit_config = { enabled: true, type: genPlan.deposit_type, value: Number(genPlan.deposit_value || 0) };
        }
        await pb.collection('invoices').update(inv.id, meta);
      } catch { }
      await loadInstallments(inv.id);
      await recomputeInvoiceFromInstallments(inv);
      alert('Échéancier généré');
    } catch (e) {
      alert('Impossible de générer l’échéancier');
    }
  };

  const markInstallmentPaid = async (it) => {
    try {
      const amount = Number(prompt('Montant payé (TTC)', String(it.amount_due || it.amount_paid || 0))) || 0;
      const method = prompt('Méthode (card/transfer/cash/check/other)', it.payment_method || 'transfer') || 'transfer';
      const ref = prompt('Référence (optionnel)', it.payment_ref || '') || '';
      const payload = {
        amount_paid: amount,
        status: amount >= Number(it.amount_due || 0) ? 'paid' : 'partial',
        paid_at: new Date().toISOString(),
        payment_method: method,
        payment_ref: ref
      };
      await pb.collection('invoice_installments').update(it.id, payload);
      await loadInstallments(it.invoice_id);
      const inv = invoices.find(x => x.id === it.invoice_id);
      if (inv) await recomputeInvoiceFromInstallments(inv);
    } catch (e) { alert('Mise à jour impossible'); }
  };

  const deleteInstallment = async (it) => {
    if (!window.confirm('Supprimer cette échéance ?')) return;
    try {
      await pb.collection('invoice_installments').delete(it.id);
      await loadInstallments(it.invoice_id);
      const inv = invoices.find(x => x.id === it.invoice_id);
      if (inv) await recomputeInvoiceFromInstallments(inv);
    } catch { alert('Suppression impossible'); }
  };

  const buildInvoicesHeaderShareText = (accountUrl) => {
    return [
      'Bonjour,',
      '',
      'Retrouvez vos factures et paiements dans votre espace client :',
      accountUrl,
      '',
      'Bien à vous,',
      'L’équipe INFOCOLAB'
    ].join('\n');
  };

  const buildInvoiceDetailedShareText = (invoice, accountUrl) => {
    const lines = [
      'Bonjour,',
      '',
      `Votre facture ${invoice.invoice_number || ''} est disponible dans votre espace client :`,
      accountUrl,
      '',
      `Montant TTC : ${typeof invoice.total === 'number' ? invoice.total.toFixed(2) : '0.00'} €`,
      `Échéance : ${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}`,
      '',
      'Vous pouvez consulter et régler votre facture depuis cet espace.',
      '',
      'Bien à vous,',
      'L’équipe INFOCOLAB'
    ];
    return lines.join('\n');
  };

  React.useEffect(() => {
    (async () => {
      setError('');
      // Settings
      try {
        const res = await pb.collection('settings').getList(1, 1, { sort: '-created' });
        const s = res?.items?.[0];
        if (s) { setSettings({ ...s, _baseUrl: pb?.baseUrl }); setSettingsId(s.id); }
      } catch { /* defaults */ }

      // Customers
      try {
        if (Array.isArray(ctxCustomers) && ctxCustomers.length) setCustomers(ctxCustomers);
        else {
          const res = await pb.collection('customers').getList(1, 200, { sort: '-created' });
          setCustomers(res?.items ?? []);
        }
      } catch (e) {
        setCustomers([]);
        setError(prev => prev || e?.message || 'Erreur chargement clients');
      }

      // Vehicles (Projects)
      try {
        if (Array.isArray(ctxVehicles) && ctxVehicles.length) setVehicles(ctxVehicles);
        else {
          const res = await pb.collection('projects').getList(1, 200, { sort: '-created' });
          setVehicles(res?.items ?? []);
        }
      } catch { setVehicles([]); }

      // Services (catalogue de prestations)
      try {
        const res = await pb.collection('services_catalog').getList(1, 200, { sort: 'name' });
        const mapped = (res?.items ?? []).map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false }));
        setServices(mapped);
      } catch { setServices([]); }

      // Products
      try {
        const res = await pb.collection('products').getList(1, 200, { sort: 'name' });
        const mapped = (res?.items ?? []).map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' }));
        setParts(mapped);
      } catch { setParts([]); }

      // Quotes
      try {
        if (Array.isArray(ctxQuotes) && ctxQuotes.length) setQuotes(ctxQuotes);
        else {
          const res = await pb.collection('quotes').getList(1, 200, { sort: '-created', expand: 'customer_id' });
          setQuotes(res?.items ?? []);
        }
      } catch { setQuotes([]); }

      // Invoices
      try {
        if (Array.isArray(ctxInvoices) && ctxInvoices.length) setInvoices(ctxInvoices);
        else {
          const res = await pb.collection('invoices').getList(1, 200, { sort: '-created', expand: 'customer_id' });
          setInvoices(res?.items ?? []);
        }
      } catch { setInvoices([]); }
    })();
  }, [ctxCustomers, ctxVehicles, ctxQuotes, ctxInvoices]);

  // Deep-link support: /admin/facturation/factures?new=1&customer=<id>
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const isNew = sp.get('new');
      const cid = sp.get('customer');
      if (isNew === '1') {
        setEditingInvoice(null);
        setOpen(true);
        if (cid) setDefaultCustomerIdFromQuery(cid);
      }
    } catch { }
  }, []);

  // Prevent empty-state flash on first refresh: allow empty only shortly after admin data finished loading
  React.useEffect(() => {
    if (adminLoading) { setAllowEmpty(false); return; }
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => { clearTimeout(t); };
  }, [adminLoading]);

  const refreshInvoices = async () => {
    try {
      const res = await pb.collection('invoices').getList(1, 200, { sort: '-created', expand: 'customer_id' });
      setInvoices(res?.items ?? []);
    } catch { }
  };

  const sanitizeInvoicePayload = (payload) => {
    const safe = { ...payload };
    // Ensure invoice_number exists if backend requires it
    if (!safe.invoice_number || typeof safe.invoice_number !== 'string' || !safe.invoice_number.trim()) {
      try {
        const prefix = settings?.invoice_prefix || 'FACT';
        const next = String((settings?.invoice_counter || 1)).padStart(4, '0');
        safe.invoice_number = `${prefix}-${next}`;
      } catch {
        safe.invoice_number = `FACT-${String(Date.now()).slice(-4)}`;
      }
    }
    // Ensure issue_date exists (YYYY-MM-DD)
    if (!safe.issue_date) {
      try { safe.issue_date = new Date().toISOString().split('T')[0]; } catch { }
    }
    // Dates: keep YYYY-MM-DD as string for PB, remove empty strings
    if (!safe.due_date) delete safe.due_date;
    if (!safe.payment_date) delete safe.payment_date;
    // Numbers
    if (typeof safe.discount_value !== 'undefined') safe.discount_value = Number(safe.discount_value || 0);
    if (typeof safe.tax_rate !== 'undefined') safe.tax_rate = Number(safe.tax_rate || settings?.default_tax_rate || 20);
    if (typeof safe.subtotal !== 'undefined') safe.subtotal = Number(safe.subtotal || 0);
    if (typeof safe.discount_total !== 'undefined') safe.discount_total = Number(safe.discount_total || 0);
    if (typeof safe.tax_amount !== 'undefined') safe.tax_amount = Number(safe.tax_amount || 0);
    if (typeof safe.total !== 'undefined') safe.total = Number(safe.total || 0);
    // Items: keep only schema fields, drop UI id
    if (Array.isArray(safe.items)) {
      safe.items = safe.items.map((it) => ({
        service_id: it.service_id || '',
        description: it.description || '',
        quantity: Number(it.quantity || 0),
        unit_price: Number(it.unit_price || 0),
        tax_rate: typeof it.tax_rate !== 'undefined' ? Number(it.tax_rate) : Number(settings?.default_tax_rate || 20),
        total: Number(
          typeof it.total !== 'undefined'
            ? it.total
            : (Number(it.quantity || 0) * Number(it.unit_price || 0))
        )
      }));
    }
    // Remove UI-only flags
    delete safe.allowEmpty;
    return safe;
  };

  const handleCreate = async (rawPayload) => {
    setLoading(true);
    try {
      const { installments: newInstallments, ...payload } = rawPayload;
      const created = await pb.collection('invoices').create(sanitizeInvoicePayload(payload));

      if (Array.isArray(newInstallments) && newInstallments.length > 0) {
        await Promise.all(newInstallments.map(ins => pb.collection('invoice_installments').create({
          ...ins,
          invoice_id: created.id,
          amount_paid: 0,
          status: 'pending'
        })));
      }

      // TRIGGER EMAIL
      const { EmailService } = require('../../../services/emailService');
      const client = customers.find(c => c.id === created.customer_id);
      if (client) EmailService.sendInvoiceCreated(created, client);

      if (settingsId) {
        try { await pb.collection('settings').update(settingsId, { invoice_counter: Number(settings.invoice_counter || 1) + 1 }); } catch { }
      }
      await refreshInvoices();
      setOpen(false);
      setEditingInvoice(null);
      setEditingInstallments([]);
      return created;
    } catch (e) {
      console.error('Create invoice failed:', e);
      let details = '';
      try {
        const d = e?.data?.data || {};
        details = Object.keys(d).map(k => `${k}: ${d[k]?.message || JSON.stringify(d[k])}`).join('\n');
      } catch { }
      try {
        alert(`Création facture impossible. Vérifiez les champs requis (client, projet, échéance, au moins une prestation).${details ? '\n\nDétails:\n' + details : ''}`);
      } catch { }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, rawPayload) => {
    setLoading(true);
    try {
      const { installments: newInstallments, ...payload } = rawPayload;
      await pb.collection('invoices').update(id, sanitizeInvoicePayload(payload));

      if (Array.isArray(newInstallments)) {
        const existing = await pb.collection('invoice_installments').getFullList({ filter: `invoice_id = "${id}"` });
        const comingIds = newInstallments.map(i => i.id).filter(Boolean);
        const toDelete = existing.filter(e => !comingIds.includes(e.id));

        await Promise.all(toDelete.map(d => pb.collection('invoice_installments').delete(d.id)));

        for (const ins of newInstallments) {
          if (ins.id && existing.find(e => e.id === ins.id)) {
            await pb.collection('invoice_installments').update(ins.id, ins);
          } else {
            const { id: _, ...rest } = ins;
            await pb.collection('invoice_installments').create({ ...rest, invoice_id: id });
          }
        }
      }

      await refreshInvoices();
      setOpen(false);
      setEditingInvoice(null);
      setEditingInstallments([]);
    } catch (e) {
      console.error('Update invoice failed:', e);
      let details = '';
      try {
        const d = e?.data?.data || {};
        details = Object.keys(d).map(k => `${k}: ${d[k]?.message || JSON.stringify(d[k])}`).join('\n');
      } catch { }
      try { alert(`Mise à jour facture impossible.${details ? '\n\nDétails:\n' + details : ''}`); } catch { }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await pb.collection('invoices').delete(id);
      await refreshInvoices();
    } catch (e) { alert(e?.message || 'Erreur suppression'); }
  };

  const loadInvoiceHistory = async (invoice) => {
    if (!invoice?.id) return;
    setInvoiceLogsLoading(true);
    try {
      const logs = await pb.collection('activity_logs').getFullList({
        filter: `collection = "invoices" && record_id = "${invoice.id}"`,
        sort: '-created'
      }).catch(() => []);
      setInvoiceLogs(logs || []);
    } catch (e) {
      console.error('Erreur lors du chargement de l\'historique facture:', e);
      setInvoiceLogs([]);
    } finally {
      setInvoiceLogsLoading(false);
    }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ marginTop: 0 }}>Factures</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-primary"
            onClick={() => { setEditingInvoice(null); setEditingInstallments([]); setOpen(true); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
              border: '1px solid rgba(79,70,229,0.6)',
              boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
              color: 'white'
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </span>
            <span>Créer une facture</span>
          </button>
        </div>
      </div>
      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .customer-avatar { width:36px; height:36px; border-radius:999px; display:grid; place-items:center; font-weight:700; background:#3b82f6; color:#fff; }
        .header-shape{ border-radius:10px; padding:8px 10px; }
        .btn-accent{ background: rgba(79,70,229,0.12); color: var(--admin-accent); border:1px solid rgba(79,70,229,0.45); }
        .btn-accent:hover{ background: rgba(79,70,229,0.22) !important; border-color: rgba(79,70,229,0.6) !important; color: var(--admin-accent) !important; }
        .btn-danger{ background: rgba(239,68,68,0.12); color:#ef4444; border:1px solid rgba(239,68,68,0.45); }
        .btn-danger:hover{ background: rgba(239,68,68,0.22) !important; border-color: rgba(239,68,68,0.6) !important; color:#ef4444 !important; }
        .btn-success{ background: rgba(16,185,129,0.12); color:#10b981; border:1px solid rgba(16,185,129,0.45); }
        .btn-success:hover{ background: rgba(16,185,129,0.22) !important; border-color: rgba(16,185,129,0.6) !important; color:#10b981 !important; }
        .btn-warning{ background: rgba(234,179,8,0.12); color:#eab308; border:1px solid rgba(234,179,8,0.45); }
        .btn-warning:hover{ background: rgba(234,179,8,0.22) !important; border-color: rgba(234,179,8,0.6) !important; color:#eab308 !important; }
        .btn-muted{ background: rgba(148,163,184,0.12); color:#94a3b8; border:1px solid rgba(148,163,184,0.45); }
        .btn-muted:hover{ background: rgba(148,163,184,0.22) !important; border-color: rgba(148,163,184,0.6) !important; color:#94a3b8 !important; }
        .has-tip{ position:relative; }
        .has-tip::after{ content: attr(data-tip); position:absolute; bottom: calc(100% + 8px); left:50%; transform: translateX(-50%); background: var(--admin-panel); color: var(--admin-text); border:1px solid var(--admin-border); border-radius:8px; padding:6px 8px; font-size:.8rem; white-space:nowrap; box-shadow: 0 8px 18px rgba(0,0,0,0.18); opacity:0; pointer-events:none; transition: opacity .15s ease; z-index: 70; }
        .has-tip::before{ content:""; position:absolute; bottom: calc(100% + 4px); left:50%; transform: translateX(-50%) rotate(45deg); width:10px; height:10px; background: var(--admin-panel); border-left:1px solid var(--admin-border); border-top:1px solid var(--admin-border); opacity:0; transition: opacity .15s ease; z-index: 70; }
        .has-tip:hover::after, .has-tip:hover::before{ opacity:1; }
        .searchbar{ position:relative; display:flex; align-items:center; gap:8px; }
        .searchbar .icon{ position:absolute; left:10px; color: var(--admin-muted); }
        .searchbar input{ width:260px; background: var(--admin-panel); border:1px solid var(--admin-border); color: var(--admin-text); border-radius:10px; padding:8px 10px 8px 30px; outline:none; transition: box-shadow .15s ease, border-color .15s ease; }
        .searchbar input::placeholder{ color: var(--admin-muted); }
        .searchbar input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        /* Group container neutral; each button framed */
        .header-actions-frame{ display:flex; gap:8px; padding:0; border:none; background: transparent; }
        .header-actions-frame .header-pill{ border:1px solid var(--admin-border); background: var(--admin-panel-2); }
        .admin-shell.admin-light .header-actions-frame .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-header h3 { margin: 0; font-size: 1.1rem; }
        .modal-body { padding: 16px 20px; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }

        /* Mobile tweaks to prevent overflow */
        @media (max-width: 720px) {
          .invoices-header{ flex-wrap: wrap; }
          .invoices-search{ width:100%; margin-left:0 !important; }
          .invoices-search .searchbar{ width:100%; }
          .invoices-search .searchbar input{ width:100%; }
        }
        @media (max-width: 480px) {
          .invoices-grid{ grid-template-columns: 1fr !important; }
        }
      `}</style>
      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="invoices-header" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="header-actions-frame">
            <button className="header-pill header-shape has-tip" data-tip="QR Espace client" title="QR Espace client" aria-label="QR Espace client" onClick={() => setShowInvoicesGeneralQR(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z" />
              </svg>
            </button>
            <button className="header-pill header-shape has-tip" data-tip="Copier le lien" onClick={async () => {
              const link = window.location.origin + '/admin/facturation/factures';
              try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch { }
            }}>Copier le lien</button>
            <button className="header-pill header-shape has-tip" data-tip="Copier le message" onClick={async () => {
              const origin = window.location.origin; const accountUrl = `${origin}/mon-compte`;
              const shareText = buildInvoicesHeaderShareText(accountUrl);
              try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { }
            }}>Copier le message</button>
            <button className="header-pill header-shape has-tip" data-tip="Partager" onClick={async () => {
              const origin = window.location.origin; const accountUrl = `${origin}/mon-compte`;
              const shareText = buildInvoicesHeaderShareText(accountUrl);
              if (navigator.share) { try { await navigator.share({ title: 'Espace client - Factures', text: shareText, url: accountUrl }); } catch { } }
              else { try { await navigator.clipboard.writeText(shareText); alert('Texte copié'); } catch { alert('Partage non supporté'); } }
            }}>Partager</button>
          </div>
          <div className="searchbar invoices-search" style={{ marginLeft: 'auto' }}>
            <svg className="icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
            <input
              type="text"
              placeholder="Rechercher une facture..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Invoices grid */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="invoices-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
          {invoices
            .filter(inv =>
              (inv.invoice_number?.toLowerCase() || '').includes(search.toLowerCase()) ||
              (customerNameOf(inv).toLowerCase()).includes(search.toLowerCase())
            )
            .map((inv) => (
              <div key={inv.id} style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="customer-avatar">
                    {(customerNameOf(inv) || '—').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.invoice_number || '—'}</div>
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customerNameOf(inv)}</div>
                    {companyNameOf(inv) && (
                      <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{companyNameOf(inv)}</div>
                    )}

                    {showInstallmentsModal && (
                      <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) setShowInstallmentsModal(false); }}>
                        <div className="modal-content">
                          <div className="modal-header">
                            <h3>Échéancier – {activeInstallmentsInvoice?.invoice_number || ''}</h3>
                            <button className="close-btn" onClick={() => setShowInstallmentsModal(false)} aria-label="Fermer">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                            </button>
                          </div>
                          <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8 }}>
                              <div>
                                <label>Nombre d’échéances</label>
                                <input type="number" min={1} value={genPlan.count} onChange={e => setGenPlan(prev => ({ ...prev, count: Number(e.target.value || 1) }))} />
                              </div>
                              <div>
                                <label>Fréquence</label>
                                <select value={genPlan.frequency} onChange={e => setGenPlan(prev => ({ ...prev, frequency: e.target.value }))}>
                                  <option value="monthly">Mensuelle</option>
                                  <option value="biweekly">Bimensuelle (14j)</option>
                                  <option value="weekly">Hebdomadaire</option>
                                </select>
                              </div>
                              <div>
                                <label>Première date</label>
                                <input type="date" value={genPlan.start_date} onChange={e => setGenPlan(prev => ({ ...prev, start_date: e.target.value }))} />
                              </div>
                              <div>
                                <label style={{ display: 'block' }}>Acompte</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input type="checkbox" checked={!!genPlan.deposit_enabled} onChange={(e) => setGenPlan(prev => ({ ...prev, deposit_enabled: !!e.target.checked }))} />
                                  {genPlan.deposit_enabled && (
                                    <>
                                      <select value={genPlan.deposit_type} onChange={(e) => setGenPlan(prev => ({ ...prev, deposit_type: e.target.value }))}>
                                        <option value="percent">%</option>
                                        <option value="fixed">€</option>
                                      </select>
                                      <input type="number" min={0} step="0.01" value={genPlan.deposit_value} onChange={(e) => setGenPlan(prev => ({ ...prev, deposit_value: Number(e.target.value || 0) }))} onWheel={(e) => e.currentTarget.blur()} style={{ maxWidth: 120 }} />
                                    </>
                                  )}
                                </div>
                              </div>
                              <div style={{ alignSelf: 'end' }}>
                                <button className="header-pill header-shape btn-accent" onClick={generateInstallments}>Générer</button>
                              </div>
                            </div>

                            <div style={{ border: '1px solid var(--admin-border)', borderRadius: 8 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 140px 140px 120px 120px', padding: '8px 10px', borderBottom: '1px solid var(--admin-border)', background: 'var(--admin-panel-2)', fontWeight: 600 }}>
                                <div>#</div><div>Échéance</div><div>Date</div><div>Montant TTC</div><div>Payé</div><div>Actions</div>
                              </div>
                              {installmentsLoading ? (
                                <div style={{ padding: 10 }}>Chargement...</div>
                              ) : installments.length === 0 ? (
                                <div style={{ padding: 10, color: 'var(--admin-muted)' }}>Aucune échéance</div>
                              ) : installments.map((it, idx) => (
                                <div key={it.id} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 140px 140px 120px 120px', padding: '8px 10px', borderBottom: '1px solid var(--admin-border)' }}>
                                  <div>{idx + 1}</div>
                                  <div>{it.label || `Échéance ${idx + 1}`}</div>
                                  <div>{it.due_date ? new Date(it.due_date).toLocaleDateString() : '-'}</div>
                                  <div>{Number(it.amount_due || 0).toFixed(2)}€</div>
                                  <div>{Number(it.amount_paid || 0).toFixed(2)}€ {it.status === 'paid' ? '✅' : it.status === 'partial' ? '⏳' : ''}</div>
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    <button className="header-pill header-shape btn-success" onClick={() => markInstallmentPaid(it)}>Encaisser</button>
                                    <button className="header-pill header-shape btn-danger" onClick={() => deleteInstallment(it)}>Supprimer</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    {(() => {
                      const now = new Date();
                      const isOverdue = inv.due_date ? (new Date(inv.due_date) < now && inv.payment_status !== 'paid') : false;
                      const ps = inv.payment_status || 'pending';
                      const map = {
                        pending: { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8', border: 'rgba(148,163,184,0.45)', label: 'En attente' },
                        partial: { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6', border: 'rgba(59,130,246,0.45)', label: 'Partiel' },
                        paid: { bg: 'rgba(16,185,129,0.12)', color: '#10b981', border: 'rgba(16,185,129,0.45)', label: 'Payé' },
                        overdue: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', border: 'rgba(239,68,68,0.45)', label: 'En retard' }
                      };
                      const meta = isOverdue ? map.overdue : (map[ps] || map.pending);
                      return (
                        <span style={{ padding: '4px 8px', borderRadius: 999, border: `1px solid ${meta.border}`, fontSize: '.8rem', background: meta.bg, color: meta.color }}>
                          {meta.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v18H3z" /></svg>
                    <span>Projet: {projectNameOf(inv) || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 14H7v-2h10v2zm0-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>
                    <span>Émise le: {inv.created ? new Date(inv.created).toLocaleDateString() : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 12h18v2H3z" /></svg>
                    <span>Total HT: {typeof inv.subtotal === 'number' ? `${inv.subtotal.toFixed(2)}€` : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5v14m7-7H5" /></svg>
                    <span>Total TTC: {typeof inv.total === 'number' ? `${inv.total.toFixed(2)}€` : '—'}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap', position: 'relative' }}>
                  <button className="header-pill header-shape btn-muted has-tip" data-tip="Aperçu PDF" title="Aperçu PDF" aria-label="Aperçu PDF" onClick={async () => {
                    try {
                      // récupérer customer/vehicle
                      let customer = customers.find(c => c.id === inv.customer_id) || inv.expand?.customer_id || null;
                      let vehicle = vehicles.find(v => v.id === inv.vehicle_id) || inv.expand?.vehicle_id || null;
                      const inst = await pb.collection('invoice_installments').getFullList({ filter: `invoice_id = "${inv.id}"`, sort: 'due_date' }).catch(() => []);
                      const gen = new PDFGenerator();
                      await gen.generateInvoicePDF(inv, customer, vehicle, settings, inst);
                      gen.preview();
                      logActivity({ recordId: inv.id, action: 'pdf_preview', message: `Aperçu PDF pour la facture ${inv.invoice_number || inv.id}` });
                    } catch (e) { console.error(e); try { alert('Aperçu PDF indisponible'); } catch { } }
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" /></svg>
                  </button>
                  <button className="header-pill header-shape has-tip" data-tip="Échéancier" title="Échéancier" aria-label="Échéancier" onClick={() => openInstallments(inv)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h12v2H3v-2z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-muted has-tip" data-tip="Télécharger PDF" title="Télécharger PDF" aria-label="Télécharger PDF" onClick={async () => {
                    try {
                      let customer = customers.find(c => c.id === inv.customer_id) || inv.expand?.customer_id || null;
                      let vehicle = vehicles.find(v => v.id === inv.vehicle_id) || inv.expand?.vehicle_id || null;
                      const inst = await pb.collection('invoice_installments').getFullList({ filter: `invoice_id = "${inv.id}"`, sort: 'due_date' }).catch(() => []);
                      const gen = new PDFGenerator();
                      await gen.generateInvoicePDF(inv, customer, vehicle, settings, inst);
                      gen.download(`Facture_${inv.invoice_number || inv.id}.pdf`);
                      logActivity({ recordId: inv.id, action: 'pdf_download', message: `Téléchargement PDF pour la facture ${inv.invoice_number || inv.id}` });
                    } catch (e) { console.error(e); try { alert('Téléchargement PDF indisponible'); } catch { } }
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-accent has-tip" data-tip="Partager lien espace client" title="Partager lien espace client" aria-label="Partager lien espace client" onClick={async () => {
                    const origin = window.location.origin; const accountUrl = `${origin}/mon-compte`;
                    const shareText = [
                      'Bonjour,', '', `Votre facture ${inv.invoice_number} est disponible dans votre espace client :`, accountUrl, '', 'Vous pouvez consulter et régler votre facture depuis cet espace.', '', 'Bien à vous,', 'L’équipe INFOCOLAB'
                    ].join('\n');
                    if (navigator.share) { try { await navigator.share({ title: `Facture ${inv.invoice_number}`, text: shareText, url: accountUrl }); } catch { } }
                    else { try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { alert('Partage non supporté'); } }
                    logActivity({ recordId: inv.id, action: 'share_link', message: `Lien espace client partagé pour la facture ${inv.invoice_number || inv.id}` });
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-muted has-tip" data-tip="Dupliquer la facture" title="Dupliquer" aria-label="Dupliquer" onClick={async () => {
                    const payload = {
                      invoice_number: '', // sera défini par le Form s'il est utilisé ou par le serveur si logique dédiée
                      customer_id: inv.customer_id || undefined,
                      vehicle_id: inv.vehicle_id || undefined,
                      quote_id: inv.quote_id || undefined,
                      status: 'draft',
                      due_date: inv.due_date || '',
                      payment_status: 'pending',
                      payment_date: '',
                      payment_method: '',
                      notes: inv.notes || '',
                      items: Array.isArray(inv.items) ? inv.items : [],
                      discount_type: inv.discount_type || '',
                      discount_value: Number(inv.discount_value || 0),
                      discount_label: inv.discount_label || '',
                      tax_enabled: typeof inv.tax_enabled === 'boolean' ? inv.tax_enabled : true,
                    };
                    try { const created = await handleCreate(payload); logActivity({ recordId: created?.id || inv.id, action: 'duplicate', message: `Facture dupliquée depuis ${inv.invoice_number || inv.id}` }); } catch { }
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14l4-4h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-success has-tip" data-tip="Marquer comme payée" title="Payée" aria-label="Payée" onClick={async () => { await handleUpdate(inv.id, { payment_status: 'paid', status: 'sent' }); logActivity({ recordId: inv.id, action: 'mark_paid', message: `Facture ${inv.invoice_number || inv.id} marquée comme payée` }); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-warning has-tip" data-tip="Historique" title="Historique" aria-label="Historique" onClick={() => { setHistoryInvoice(inv); loadInvoiceHistory(inv); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.06 4.94L7.64 18.36A9 9 0 1 0 13 3zm-1 4v5l4.25 2.52.75-1.23-3.5-2.08V7H12z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-accent has-tip" data-tip="Modifier la facture" title="Modifier" aria-label="Modifier" onClick={async () => {
                    setEditingInvoice(inv);
                    try {
                      const inst = await pb.collection('invoice_installments').getFullList({ filter: `invoice_id="${inv.id}"`, sort: 'due_date' });
                      setEditingInstallments(inst);
                    } catch {
                      setEditingInstallments([]);
                    }
                    setOpen(true);
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-danger has-tip" data-tip="Supprimer la facture" title="Supprimer" aria-label="Supprimer" onClick={() => handleDelete(inv.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                  </button>
                  <div style={{ marginLeft: 'auto', color: 'var(--admin-muted)', fontSize: '.85rem' }}>Créée le {new Date(inv.created).toLocaleDateString()}</div>
                </div>
              </div>
            ))}

          {invoices.filter(inv => (inv.invoice_number?.toLowerCase() || '').includes(search.toLowerCase()) || ((inv.expand?.customer_id?.name || '').toLowerCase()).includes(search.toLowerCase())).length === 0 && !adminLoading && allowEmpty && (
            <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .7, display: 'block' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <h3 style={{ margin: 0 }}>Aucune facture trouvée</h3>
                <p style={{ margin: 0, color: 'var(--admin-muted)' }}>
                  {search ? `Aucun résultat pour "${search}"` : 'Commencez par créer votre première facture'}
                </p>
                {!search && (
                  <button
                    className="btn-primary"
                    onClick={() => { setEditingInvoice(null); setEditingInstallments([]); setOpen(true); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', borderRadius: 10,
                      background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                      border: '1px solid rgba(79,70,229,0.6)',
                      boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                      color: 'white'
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </span>
                    <span>Nouvelle facture</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <InvoiceForm
          invoice={editingInvoice}
          customers={customers}
          vehicles={vehicles}
          services={services}
          parts={parts}
          settings={settings}
          quotes={quotes}
          initialInstallments={editingInstallments || []}
          onSubmit={(data) => editingInvoice ? handleUpdate(editingInvoice.id, data) : handleCreate(data)}
          onCancel={() => { setOpen(false); setEditingInvoice(null); setEditingInstallments([]); }}
          loading={loading}
          onCreateCustomer={async (data) => { const r = await pb.collection('customers').create(data); try { const list = await pb.collection('customers').getList(1, 200, { sort: '-created' }); setCustomers(list?.items ?? []); } catch { } return r; }}
          onCreateVehicle={async (data) => { const cleaned = { ...data }; const linkId = cleaned.client || cleaned.customer_id || cleaned.customer || (customers[0]?.id) || null; if (linkId) { cleaned.client = linkId; cleaned.customer_id = linkId; cleaned.customer = linkId; } const r = await pb.collection('projects').create(cleaned); try { const list = await pb.collection('projects').getList(1, 200, { sort: '-created' }); setVehicles(list?.items ?? []); } catch { } return r; }}
          onCreateService={async (data) => { const r = await pb.collection('services_catalog').create(data); try { const list = await pb.collection('services_catalog').getList(1, 200, { sort: 'name' }); const mapped = (list?.items ?? []).map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })); setServices(mapped); } catch { } return r; }}
          onCreatePart={async (data) => { const r = await pb.collection('products').create(data); try { const list = await pb.collection('products').getList(1, 200, { sort: 'name' }); const mapped = (list?.items ?? []).map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' })); setParts(mapped); } catch { } return r; }}
          defaultCustomerId={defaultCustomerIdFromQuery || customers[0]?.id}
        />
      )}

      {showInvoicesGeneralQR && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) setShowInvoicesGeneralQR(false); }}>
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3>QR code – Espace client</h3>
              <button className="close-btn" onClick={() => setShowInvoicesGeneralQR(false)} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
              {(() => {
                const url = `${window.location.origin}/mon-compte`; const qr = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(url)}`; return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <img src={qr} alt="QR Espace client" width={240} height={240} style={{ borderRadius: 12, border: '1px solid var(--admin-border)', background: 'white' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button className="header-pill header-shape btn-accent" onClick={async () => { try { await navigator.clipboard.writeText(url); alert('Lien copié'); } catch { } }}>Copier le lien</button>
                      <button className="header-pill header-shape" onClick={() => { try { const a = document.createElement('a'); a.href = qr; a.download = 'qr-espace-client.png'; document.body.appendChild(a); a.click(); a.remove(); } catch { } }}>Télécharger QR</button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {historyInvoice && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) setHistoryInvoice(null); }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Historique de la facture {historyInvoice.invoice_number || ''}</h3>
              <button className="close-btn" onClick={() => setHistoryInvoice(null)} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body">
              {invoiceLogsLoading ? (
                <div>Chargement...</div>
              ) : invoiceLogs.length === 0 ? (
                <div>Aucun historique pour cette facture.</div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {invoiceLogs.map(log => (
                    <div key={log.id} style={{ border: '1px solid var(--admin-border)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontWeight: 600 }}>{new Date(log.created).toLocaleString()}</div>
                      <div style={{ color: 'var(--admin-muted)' }}>{log.message || '-'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminBillingInvoicesPage;
